import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';

const SCRAPER_API = 'https://api.shippix.pt/scraper.php';
const AMAZON_SCRAPER_API = 'https://api.shippix.pt/amazon_scraper.php';
const KUANTOKUSTA_SCRAPER_API = 'https://api.shippix.pt/kuantokusta_scraper.php';
const FNAC_SCRAPER_API = 'https://api.shippix.pt/scraper_fnac.php';

// Lista de competidores suportados
export type Competitor = 'continente' | 'auchan' | 'pingodoce' | 'worten' | 'amazon' | 'kuantokusta' | 'fnac';

interface ScraperResponse {
  success: boolean;
  price?: number;
  error?: string;
}

// Função para extrair o competidor da URL
function getCompetitorFromUrl(url: string): Competitor {
  const urlObj = new URL(url);
  const hostname = urlObj.hostname;
  
  if (hostname.includes('continente')) return 'continente';
  if (hostname.includes('auchan')) return 'auchan';
  if (hostname.includes('pingodoce')) return 'pingodoce';
  if (hostname.includes('worten.pt')) return 'worten';
  if (hostname.includes('amazon')) return 'amazon';
  if (hostname.includes('kuantokusta')) return 'kuantokusta';
  if (hostname.includes('fnac.pt')) return 'fnac';
  
  throw new Error('Competidor não suportado: ' + hostname);
}

export async function scrapeUrl(url: string): Promise<string> {
  try {
    console.log('Iniciando scraping da URL:', url);
    
    // Criar um objeto URL para garantir que a URL está bem formada
    const targetUrl = new URL(url);
    
    // Identificar o competidor pela URL
    const competitor = getCompetitorFromUrl(url);
    
    // Escolher a API correta baseado no competidor
    let apiUrl: URL;
    if (competitor === 'amazon') {
      apiUrl = new URL(AMAZON_SCRAPER_API);
    } else if (competitor === 'kuantokusta') {
      apiUrl = new URL(KUANTOKUSTA_SCRAPER_API);
    } else if (competitor === 'fnac') {
      apiUrl = new URL(FNAC_SCRAPER_API);
    } else {
      apiUrl = new URL(SCRAPER_API);
    }
    
    // Adicionar parâmetros usando URLSearchParams
    if (competitor !== 'fnac') {
      apiUrl.searchParams.append('url', targetUrl.toString());
      if (competitor !== 'amazon' && competitor !== 'kuantokusta') {
        apiUrl.searchParams.append('competitor', competitor);
      }
    }
    
    console.log('URL da API:', apiUrl.toString());

    const response = await fetch(apiUrl.toString(), {
      method: competitor === 'fnac' ? 'POST' : 'GET',
      headers: {
        'Accept': 'application/json',
        ...(competitor === 'fnac' && {
          'Content-Type': 'application/x-www-form-urlencoded'
        })
      },
      ...(competitor === 'fnac' && {
        body: `url=${encodeURIComponent(targetUrl.toString())}`
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
      throw new Error(`Erro no scraping: ${errorData.error || response.statusText}`);
    }

    const data: ScraperResponse = await response.json();
    console.log('Resposta do scraper:', data);
    
    if (!data.success) {
      throw new Error(`Erro no scraping: ${data.error}`);
    }

    // Garantir que o preço é retornado como string, mesmo que seja um número na resposta
    return data.price?.toString() || '';

  } catch (error) {
    console.error('Erro no scraping:', error);
    throw error;
  }
}

export async function updateCompetitorPrice(url: string): Promise<number | null> {
  try {
    console.log('Iniciando atualização de preço para URL:', url);
    const priceStr = await scrapeUrl(url);
    if (!priceStr) return null;
    
    const price = parseFloat(priceStr);
    if (isNaN(price)) {
      console.error('Preço inválido retornado pela API:', priceStr);
      return null;
    }

    return price;
  } catch (error) {
    console.error('Erro ao atualizar preço:', error);
    return null;
  }
}
